/**
 * Platform detection — single source of truth for runtime environment.
 *
 * Import from `@/platform` instead of checking `window.__SONGBIRD_WASM_MODE__`
 * or `window.__TAURI_INTERNALS__` directly.
 */

export type PlatformType = 'tauri' | 'wasm' | 'headless';

let _cached: PlatformType | null = null;

/**
 * Detect the current runtime platform.
 *
 * - `tauri`    — Tauri v2 desktop shell (`__TAURI_INTERNALS__` present)
 * - `wasm`     — WASM web build (`__SONGBIRD_WASM_MODE__` set by wasmBridge)
 * - `headless` — WebSocket headless server (fallback)
 */
export function getPlatform(): PlatformType {
  if (_cached) return _cached;
  if (typeof window === 'undefined') {
    _cached = 'headless';
    return _cached;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__SONGBIRD_WASM_MODE__) {
    _cached = 'wasm';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } else if ((window as any).__TAURI_INTERNALS__) {
    _cached = 'tauri';
  } else {
    _cached = 'headless';
  }
  return _cached;
}

/** True when running inside the WASM web build. */
export function isWasm(): boolean { return getPlatform() === 'wasm'; }

/** True when running inside the Tauri desktop shell. */
export function isTauri(): boolean { return getPlatform() === 'tauri'; }

/** True on Android/iOS or when `?mode=mobile` is set. */
export function isMobile(): boolean {
  const params = new URLSearchParams(location.search);
  return params.get('mode') === 'mobile'
    || /Android|iPhone|iPad/.test(navigator.userAgent);
}

/** Tauri internals — typed subset of the `__TAURI_INTERNALS__` global. */
export interface TauriInternals {
  invoke: (cmd: string, args?: unknown) => Promise<unknown>;
  convertFileSrc?: (path: string, protocol?: string) => string;
}

/**
 * Get the Tauri internals object, or `null` if not in Tauri.
 *
 * Use this instead of `(window as any).__TAURI_INTERNALS__` to get
 * type-safe access to `invoke()` and `convertFileSrc()`.
 */
export function getTauriInternals(): TauriInternals | null {
  if (!isTauri()) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).__TAURI_INTERNALS__ as TauriInternals;
}
